import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

interface User {
  id: number;
  email: string;
  role: string;
  company_id?: number;
  mfa_enabled?: boolean;
  mfa_method?: 'email' | 'totp';
  slack_user_id?: string;
  name?: string;
  quiet_time_start?: string;
  quiet_time_end?: string;
  quiet_timezone?: string;
  out_of_office?: boolean;
  agent_of_last_resort?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, mfaCode?: string, rememberDevice?: boolean) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const updateUser = (userData: Partial<User>) => {
    setUser(prevUser => prevUser ? { ...prevUser, ...userData } : null);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then((response) => {
          setUser(response.data.user);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, mfaCode?: string, rememberDevice?: boolean) => {
    const response = await api.post('/auth/login', { email, password, mfaCode, rememberDevice });
    
    // If MFA setup is required, throw a special error that the Login component can handle
    if (response.data.requiresMfaSetup) {
      const error: any = new Error('MFA setup required');
      error.requiresMfaSetup = true;
      throw error;
    }
    
    // If MFA is required, throw a special error that the Login component can handle
    if (response.data.requiresMfa) {
      const error: any = new Error('MFA code required');
      error.requiresMfa = true;
      error.mfaMethod = response.data.mfaMethod || 'email';
      throw error;
    }
    
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
